import * as THREE from 'three';
import { gsap } from 'gsap';

export const setupInteractions = ({ scene, camera, canvas, serviceNodes, onServiceClick, isModalOpen }) => {
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let lastServiceHovered = null;
  const keyState = {};
  const movementDirection = new THREE.Vector3();

  // Mouse interaction handlers
  const onMouseMove = (event) => {
    // Calculate mouse position in normalized device coordinates
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    // Update the picking ray
    raycaster.setFromCamera(mouse, camera);
    
    // Calculate intersections
    const intersects = raycaster.intersectObjects(scene.children, true);
    
    // Check if hovering over a service node
    let isHoveringService = false;
    
    for (let i = 0; i < intersects.length; i++) {
      let object = intersects[i].object;
      while (object && !object.userData.isService) {
        object = object.parent;
      }
      
      if (object && object.userData.isService) {
        isHoveringService = true;
        
        if (lastServiceHovered && lastServiceHovered !== object) {
          // Reset last hovered service
          gsap.to(lastServiceHovered.scale, {
            x: 1,
            y: 1,
            z: 1,
            duration: 0.3
          });
        }
        
        lastServiceHovered = object;
        
        // Scale up the hovered service node
        gsap.to(object.scale, {
          x: 1.2,
          y: 1.2,
          z: 1.2,
          duration: 0.3
        });
        
        // Change cursor to pointer
        document.body.style.cursor = 'pointer';
        break;
      }
    }
    
    if (!isHoveringService && lastServiceHovered) {
      // Reset last hovered service
      gsap.to(lastServiceHovered.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.3
      });
      
      lastServiceHovered = null;
      document.body.style.cursor = 'auto';
    }
  };

  const onMouseClick = (event) => {
    // Calculate mouse position
    mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
    
    // Update the picking ray
    raycaster.setFromCamera(mouse, camera);
    
    // Calculate intersections
    const intersects = raycaster.intersectObjects(scene.children, true);
    
    for (let i = 0; i < intersects.length; i++) {
      let object = intersects[i].object;
      while (object && !object.userData.isService) {
        object = object.parent;
      }
      
      if (object && object.userData.isService) {
        // Trigger service click callback
        onServiceClick(object.userData);
        break;
      }
    }
  };

  // Keyboard interaction handlers
  const onKeyDown = (e) => {
    keyState[e.code] = true;
  };

  const onKeyUp = (e) => {
    keyState[e.code] = false;
  };

  const updateMovementFromKeyboard = () => {
    if (isModalOpen) return;
    
    movementDirection.set(0, 0, 0);
    
    if (keyState['KeyW'] || keyState['ArrowUp']) {
      movementDirection.z = -1;
    }
    if (keyState['KeyS'] || keyState['ArrowDown']) {
      movementDirection.z = 1;
    }
    if (keyState['KeyA'] || keyState['ArrowLeft']) {
      movementDirection.x = -1;
    }
    if (keyState['KeyD'] || keyState['ArrowRight']) {
      movementDirection.x = 1;
    }
    
    // Normalize if moving diagonally
    if (movementDirection.length() > 1) {
      movementDirection.normalize();
    }
  };

  const updatePlayerMovement = (controls) => {
    if (movementDirection.length() === 0) return;
    
    // Convert movement direction from local to global coordinates
    const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(camera.quaternion);
    forward.y = 0;
    forward.normalize();
    
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(camera.quaternion);
    right.y = 0;
    right.normalize();
    
    const moveVector = new THREE.Vector3();
    moveVector.addScaledVector(forward, -movementDirection.z * 0.1);
    moveVector.addScaledVector(right, movementDirection.x * 0.1);
    
    // Calculate new positions
    const newCameraPos = camera.position.clone().add(moveVector);
    const newTargetPos = controls.target.clone().add(moveVector);
    
    // TV screen boundaries (matching environment.js screenBounds)
     const bounds = {
       minX: -3, maxX: 3,
       minY: -0.5, maxY: 4,
       minZ: -7, maxZ: -3
     };
    
    // Constrain camera position within TV screen boundaries
    if (newCameraPos.x >= bounds.minX && newCameraPos.x <= bounds.maxX &&
        newCameraPos.y >= bounds.minY && newCameraPos.y <= bounds.maxY &&
        newCameraPos.z >= bounds.minZ && newCameraPos.z <= bounds.maxZ) {
      controls.target.add(moveVector);
      camera.position.add(moveVector);
    }
  };

  // Movement animation loop
  const animateMovement = (controls) => {
    updateMovementFromKeyboard();
    updatePlayerMovement(controls);
    requestAnimationFrame(() => animateMovement(controls));
  };

  // Add event listeners
  canvas.addEventListener('mousemove', onMouseMove);
  canvas.addEventListener('click', onMouseClick);
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);

  // Cleanup function
  const cleanup = () => {
    canvas.removeEventListener('mousemove', onMouseMove);
    canvas.removeEventListener('click', onMouseClick);
    window.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('keyup', onKeyUp);
  };

  return {
    animateMovement,
    cleanup
  };
};